import type {
  CreateItemRequest,
  FieldReplacement,
  ItemListResponse,
  ItemRow,
  PatchItemRequest,
  QueryItemsWithViewRequest,
} from '@lens/shared';
import { apiRequest } from '../../shared/api/client.ts';

/**
 * HTTP-обёртки над эндпоинтами `/api/spaces/:id/items/*`.
 * M5+M6. См. docs/api-design.md §4.7.
 */

const base = (spaceId: string) => `/api/spaces/${spaceId}/items`;

export async function queryItems(
  spaceId: string,
  query: QueryItemsWithViewRequest,
): Promise<ItemListResponse> {
  return apiRequest<ItemListResponse>(`${base(spaceId)}/query`, {
    method: 'POST',
    body: query,
  });
}

export async function getItem(spaceId: string, itemId: string): Promise<ItemRow> {
  return apiRequest<ItemRow>(`${base(spaceId)}/${itemId}`, { method: 'GET' });
}

export type ItemRowWithRefs = ItemRow & { resolvedRefs?: Record<string, string> };

export async function createItem(spaceId: string, data: CreateItemRequest): Promise<ItemRowWithRefs> {
  return apiRequest<ItemRowWithRefs>(base(spaceId), { method: 'POST', body: data });
}

export async function patchItem(
  spaceId: string,
  itemId: string,
  data: PatchItemRequest,
): Promise<ItemRowWithRefs> {
  return apiRequest<ItemRowWithRefs>(`${base(spaceId)}/${itemId}`, { method: 'PATCH', body: data });
}

export async function deleteItem(spaceId: string, itemId: string): Promise<void> {
  await apiRequest<void>(`${base(spaceId)}/${itemId}`, { method: 'DELETE' });
}

/**
 * Ручной порядок карточек канбана (общий для всех пользователей). `orderedIds` —
 * желаемый порядок подмножества записей (карточки одной колонки). Бэкенд
 * переставляет их по занятым слотам `sort_order`, не трогая остальные записи.
 */
export async function reorderItems(spaceId: string, orderedIds: string[]): Promise<void> {
  await apiRequest<void>(`${base(spaceId)}/reorder`, {
    method: 'POST',
    body: { orderedIds },
  });
}

export type ChangeItemTypeResult = ItemRowWithRefs & {
  typeChange: { carried: string[]; dropped: string[] };
};

/**
 * Смена типа записи с сохранением комментариев, истории и связей.
 * Совместимые поля переносятся по slug; потери возвращаются в typeChange.dropped.
 */
export async function changeItemType(
  spaceId: string,
  itemId: string,
  itemTypeId: string,
): Promise<ChangeItemTypeResult> {
  return apiRequest<ChangeItemTypeResult>(`${base(spaceId)}/${itemId}/type`, {
    method: 'PATCH',
    body: { itemTypeId },
  });
}

// ─── M10: Bulk operations ───────────────────────────────────────────────────

export async function bulkUpdateItems(
  spaceId: string,
  data: { itemIds: string[]; fields: Record<string, unknown>; replacements?: FieldReplacement[] },
): Promise<{ updated: number; skipped: number; errors: Array<{ itemId: string; message: string }> }> {
  return apiRequest<{ updated: number; skipped: number; errors: Array<{ itemId: string; message: string }> }>(
    `${base(spaceId)}/bulk-update`,
    {
      method: 'POST',
      body: data,
    },
  );
}

export async function bulkDeleteItems(
  spaceId: string,
  data: { itemIds: string[] },
): Promise<{ deleted: number }> {
  return apiRequest<{ deleted: number }>(`${base(spaceId)}/bulk-delete`, {
    method: 'POST',
    body: data,
  });
}

export async function duplicateItem(
  spaceId: string,
  itemId: string,
  overrides?: Record<string, unknown>,
  replacements?: FieldReplacement[],
): Promise<ItemRowWithRefs> {
  const body: { overrides?: Record<string, unknown>; replacements?: FieldReplacement[] } = {};
  if (overrides && Object.keys(overrides).length > 0) body.overrides = overrides;
  if (replacements && replacements.length > 0) body.replacements = replacements;
  return apiRequest<ItemRowWithRefs>(`${base(spaceId)}/${itemId}/duplicate`, {
    method: 'POST',
    body,
  });
}

export async function bulkDuplicateItems(
  spaceId: string,
  data: { itemIds: string[]; overrides?: Record<string, unknown>; replacements?: FieldReplacement[] },
): Promise<{ duplicated: number; itemIds: string[] }> {
  return apiRequest<{ duplicated: number; itemIds: string[] }>(
    `${base(spaceId)}/duplicate-bulk`,
    { method: 'POST', body: data },
  );
}

export async function exportItemsCsv(
  spaceId: string,
  itemTypeId: string,
): Promise<void> {
  const url = `/api/spaces/${spaceId}/export/csv?itemTypeId=${itemTypeId}`;
  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) throw new Error('Export failed');
  const blob = await resp.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'export.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function importItemsCsv(
  spaceId: string,
  data: {
    itemTypeId: string;
    mapping: Record<string, string>;
    csv: string;
    defaults?: Record<string, unknown>;
    /**
     * Если задан непустой массив — импорт работает в режиме upsert:
     * запись, у которой ВСЕ перечисленные поля совпадают со значениями
     * строки, обновляется, иначе создаётся.
     * Спец-значение `__id__` (только как единственный элемент) означает
     * upsert по `id` записи.
     */
    keyFieldSlugs?: string[];
  },
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(`/api/spaces/${spaceId}/import/csv`, {
    method: 'POST',
    body: data,
  });
}

// ─── M11: Cross-links ───────────────────────────────────────────────────────

export async function copyItemToSpace(
  spaceId: string,
  itemId: string,
  data: { targetSpaceId: string; targetItemTypeId: string; linkFieldSlug?: string },
): Promise<ItemRow> {
  return apiRequest<ItemRow>(`${base(spaceId)}/${itemId}/copy-to-space`, {
    method: 'POST',
    body: data,
  });
}
